/**
 * /app/$tenantSlug/shifts/roster — Roster (HR / manager).
 *
 * Week grid that assigns employees to shifts for a date. HR rosters the whole
 * entity; a manager rosters their reports (server-enforced). v1 is a simple
 * assign form + a week list rather than drag-to-assign (the grid polish is a
 * follow-up); the API is bulk-upsert so a richer grid drops in later.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRoster, useShifts, useUpsertRoster } from "@/hooks/use-shifts";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/shifts/roster",
)({
  component: RosterRoute,
});

function RosterRoute() {
  const range = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 6);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  }, []);
  const roster = useRoster(range);
  const shifts = useShifts();
  const upsert = useUpsertRoster();

  const [empId, setEmpId] = useState("");
  const [date, setDate] = useState("");
  const [shiftId, setShiftId] = useState("");

  const shiftName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of shifts.data?.shifts ?? []) m.set(s.id, s.name);
    return m;
  }, [shifts.data]);

  async function assign() {
    const employee_id = Number(empId);
    const shift_id = Number(shiftId);
    if (!employee_id || !date || !shift_id) {
      toast.error("Fill employee, date and shift");
      return;
    }
    try {
      await upsert.mutateAsync([{ employee_id, work_date: date, shift_id }]);
      toast.success("Roster updated");
      setEmpId("");
    } catch {
      toast.error("Failed to assign");
    }
  }

  return (
    <TimeAndLeaveShell
      title="Roster"
      description="Assign employees to shifts for the week."
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Assign</CardTitle>
        </CardHeader>
        <CardContent className="grid items-end gap-3 sm:grid-cols-4">
          <div>
            <Label htmlFor="eid">Employee id</Label>
            <Input id="eid" type="number" value={empId} onChange={(e) => setEmpId(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="rd">Date</Label>
            <Input id="rd" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label>Shift</Label>
            <Select value={shiftId} onValueChange={setShiftId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a shift" />
              </SelectTrigger>
              <SelectContent>
                {(shifts.data?.shifts ?? []).map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={assign} disabled={upsert.isPending}>
            Assign
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>This week</CardTitle>
        </CardHeader>
        <CardContent>
          {roster.data && roster.data.roster.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead>Via</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roster.data.roster.map((ra) => (
                  <TableRow key={ra.id}>
                    <TableCell>{ra.work_date}</TableCell>
                    <TableCell>#{ra.employee_id}</TableCell>
                    <TableCell>{shiftName.get(ra.shift_id) ?? `#${ra.shift_id}`}</TableCell>
                    <TableCell>{ra.assigned_via}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm">No assignments this week.</p>
          )}
        </CardContent>
      </Card>
      </div>
    </TimeAndLeaveShell>
  );
}
